import { useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import { articles } from "@/mocks/articleData";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://hoobuy.christmas";

function buildArticleJsonLd(article: (typeof articles)[number]) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `${siteUrl}/artigo/${article.slug}/#article`,
    "headline": article.title,
    "description": article.description,
    "image": article.image,
    "datePublished": "2026-06-25T00:00:00+00:00",
    "dateModified": "2026-06-25T00:00:00+00:00",
    "author": {
      "@type": "Organization",
      "name": "HoobuySpreadsheet",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "HoobuySpreadsheet",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/879fae149d7d175a673508cd1be74143.jpeg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/artigo/${article.slug}`
    },
    "inLanguage": "pt-PT",
    "isPartOf": {
      "@type": "Blog",
      "@id": `${siteUrl}/#blog`,
      "name": "HoobuySpreadsheet Blog",
      "url": siteUrl
    }
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const effectiveSlug = slug || "guia-completo-hoobuyspreadsheet";
  const article = articles.find((a) => a.slug === effectiveSlug);
  const navigate = useNavigate();
  const proseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [effectiveSlug]);

  // Intercept internal links inside rendered HTML to use SPA routing
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href="/"]');
      if (link) {
        e.preventDefault();
        navigate('/');
      }
    };
    const el = proseRef.current;
    if (el) {
      el.addEventListener('click', handleClick);
      return () => el.removeEventListener('click', handleClick);
    }
  }, [navigate]);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-28 md:pt-36 pb-16 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Artigo não encontrado</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 font-medium hover:underline">
            ← Voltar ao início
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const jsonLd = buildArticleJsonLd(article);

  return (
    <main className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      {/* Article Header */}
      <header className="pt-28 md:pt-36 pb-10 md:pb-14 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <nav className="text-xs text-foreground-400 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground-700 transition-colors">Início</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground-600">Guia de Compras</span>
            <span className="mx-2">/</span>
            <span className="text-foreground-500">{article.title.substring(0, 50)}...</span>
          </nav>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
            {article.title}
          </h1>
          <p className="mt-4 text-sm text-foreground-400">
            Atualizado em {article.date} &middot; {article.readTime}
          </p>
        </div>
      </header>

      {/* Featured Image */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 -mt-4 mb-10">
        <div className="w-full aspect-[16/9] rounded-2xl overflow-hidden bg-background-100">
          <img
            src={article.image}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 md:px-8 pb-16 md:pb-24">
        <div className="prose-custom space-y-8 text-foreground-700 leading-relaxed" ref={proseRef}>

          {article.sections.map((section, sIdx) => (
            <section key={sIdx}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-12 mb-4">
                {section.heading}
              </h2>
              {section.paragraphs.map((para, pIdx) => (
                <p
                  key={pIdx}
                  className={pIdx > 0 ? "mt-4" : ""}
                  dangerouslySetInnerHTML={{ __html: para }}
                />
              ))}
            </section>
          ))}

          {/* CTA Button */}
          <div className="mt-10 flex flex-col items-center gap-3 p-8 rounded-2xl bg-background-100 border border-background-200/70">
            <p className="text-lg font-semibold text-foreground-900 text-center">
              Pronto para começar a poupar?
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-foreground-950 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-all duration-200 cursor-pointer whitespace-nowrap group"
            >
              <span>Aceder ao HoobuySpreadsheet Agora</span>
              <i className="ri-arrow-right-up-line text-base group-hover:translate-x-0.5 transition-transform"></i>
            </a>
            <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors">
              ← Voltar à página inicial
            </Link>
          </div>

        </div>
      </article>

      {/* Related Articles */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            Explore Mais Artigos
          </h2>
          <p className="text-foreground-500 mb-8">
            Descubra mais guias, dicas e comparações no nosso blog.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {articles.filter((a) => a.slug !== slug).slice(0, 4).map((a) => (
              <Link
                key={a.slug}
                to={`/artigo/${a.slug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-background-200 text-foreground-700 text-sm font-medium rounded-full hover:bg-background-50 hover:border-foreground-300 transition-all cursor-pointer whitespace-nowrap"
              >
                {a.title.substring(0, 45)}...
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            ))}
          </div>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 mt-8 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Ver Todas as Categorias
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}